import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { AimsFile, Deficiency, DeficiencyFileEdited } from '../../models/models';
import { DeficiencyFilesDocument } from '../../rxdb/deficiencyFiles/queryBuilder';
import { useFileStorage, useNotification } from '../../utilities';
import { INVALID_FILE, SPECIAL_CHARACTER_ERROR } from '../../utilities/constants';
import {
  autoVersionIfFileExists,
  checkFileCorruption,
  groupObjectArrayByKey,
  hasSpecialCharacter,
} from '../../utilities/utilityFunctions';
import FileSelectionView from '../FileSelectionView/FileSelectionView';

interface Props {
  onClose: () => void;
  editFile: Deficiency;
  isEditable: boolean;
  isActiveDeficiency: boolean;
}

const AddPhotoDialog: React.FC<Props> = ({
  onClose,
  editFile,
  isEditable,
  isActiveDeficiency,
}: Props) => {
  const { uploadDeficiencyFile } = useFileStorage();
  const { errorNotification } = useNotification();
  const { CurrentUserSelected } = useMst();
  const [filesToUpload, setFilesToUpload] = useState<AimsFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<AimsFile[]>([]);
  const [openFileUploadView, setOpenFileUploadView] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deficiencyFiles, setDeficiencyFiles] = useState<
    (DeficiencyFileEdited | DeficiencyFilesDocument)[]
  >([]);
  const componentVersionInspectionId = isActiveDeficiency
    ? editFile?.componentVersionInspectionId === CurrentUserSelected.inspectionId
      ? editFile?.componentVersionInspectionId
      : CurrentUserSelected.inspectionId
    : null;
  const componentVersionId = isActiveDeficiency
    ? componentVersionInspectionId === null
      ? null
      : editFile?.componentVersionId
    : null;

  const handleFileUploadViewClose = async () => {
    setSelectedFiles([]);
    setFilesToUpload([]);
    setDeficiencyFiles([]);
    onClose();
    setOpenFileUploadView(false);
  };

  const handleImageUploads = async (fileList: AimsFile[]) => {
    const files = [...fileList];
    const existingFilesGrouped = groupObjectArrayByKey(deficiencyFiles, 'showOnReport');
    const filesToUploadGrouped = groupObjectArrayByKey(files, 'showOnReport');
    setIsLoading(true);

    await Promise.all(
      Object.keys(filesToUploadGrouped).map(key => {
        filesToUploadGrouped[key].map((fileToUpload: AimsFile, index: number) => {
          filesToUploadGrouped[key][index].fileSortOrder =
            existingFilesGrouped !== undefined && existingFilesGrouped[key]?.length > 0
              ? existingFilesGrouped[key].length
              : index;
          uploadDeficiencyFile(
            fileToUpload,
            editFile.componentFailureId,
            editFile.pkDeficiencyUUID,
            isActiveDeficiency,
            componentVersionInspectionId,
            componentVersionId,
            editFile?.isAdhoc,
          );
        });
      }),
    )
      .catch(error => {
        throw new Error('HandleImageUploads error', error);
      })
      .finally(() => {
        handleFileUploadViewClose();
        setIsLoading(false);
      });
  };

  const onOpenAddUpload = () => {
    document.getElementById('addPhotoDeficiency')?.click();
  };
  const onChangeFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    let checkIFFile = false;
    let validItem = null;
    if (files !== null) {
      for (let i = 0; i < files.length; i++) {
        if (hasSpecialCharacter(files[i].name)) {
          errorNotification(SPECIAL_CHARACTER_ERROR);
          return;
        }
        validItem = await checkFileCorruption([files[i]]);
        if (!validItem.length) {
          errorNotification(INVALID_FILE);
          return;
        }
      }
      const listOfFiles: AimsFile[] = Array.from(files as unknown as AimsFile[]);
      listOfFiles.forEach((file: AimsFile, index: number) => {
        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
          alert('Only images are allowed');
          checkIFFile = true;
          return;
        }
        listOfFiles[index].updatedName = autoVersionIfFileExists(file.name, selectedFiles);
        listOfFiles[index].fileDescription = '';
        listOfFiles[index].showOnReport = false;
      });
      if (!checkIFFile) {
        setSelectedFiles([...selectedFiles, ...listOfFiles]);
      }
      if (listOfFiles.length > 0 && !openFileUploadView) {
        setOpenFileUploadView(true);
      }
    }
  };

  const checkShowOnReportCount = () => {
    const filesToUploadGrouped = groupObjectArrayByKey(filesToUpload, 'showOnReport');
    const deficiencyFilesGrouped = groupObjectArrayByKey(deficiencyFiles, 'showOnReport');
    const selectedFilesGrouped = groupObjectArrayByKey(selectedFiles, 'showOnReport');
    let countOfShowOnReportFiles = 0;
    if (filesToUploadGrouped?.true?.length) {
      countOfShowOnReportFiles += filesToUploadGrouped?.true?.length;
    }
    if (deficiencyFilesGrouped?.true?.length) {
      countOfShowOnReportFiles += deficiencyFilesGrouped?.true?.length;
    }
    if (selectedFilesGrouped?.true?.length) {
      countOfShowOnReportFiles += selectedFilesGrouped?.true?.length;
    }
    return countOfShowOnReportFiles >= 5;
  };

  useEffect(() => {
    if (editFile?.name) {
      setDeficiencyFiles([...deficiencyFiles, ...editFile?.deficiencyFiles]);
    }
  }, [editFile]);

  return (
    <>
      {openFileUploadView && (
        <FileSelectionView
          selectedFilesProps={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          setFilesToUpload={setFilesToUpload}
          filesToUpload={filesToUpload}
          onOpenFileUpload={onOpenAddUpload}
          openFileUploadView={openFileUploadView}
          handleFileUploadViewClose={handleFileUploadViewClose}
          checkShowOnReportCount={checkShowOnReportCount}
          isDirectFileUpload
          handleDirectFileUpload={(files: AimsFile[]) => handleImageUploads(files)}
          isLoading={isLoading}
          isEditable={isEditable}
        />
      )}

      <input
        type="file"
        accept=".png,.jpg,.jpeg"
        onChange={event => onChangeFilesSelected(event)}
        onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
          const element = event.target as HTMLInputElement;
          element.value = '';
        }}
        multiple
        hidden
        id="addPhotoDeficiency"
        data-testid="add-deficiency-files-card"
      />
    </>
  );
};

export default AddPhotoDialog;
